import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ResendVerification() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setSubmitted(true);
      toast.success('Verification email sent if account exists');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Please wait 2 minutes before requesting another email');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send verification email');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-apple p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-stone-900">Bella Casa Interiors</h1>
            <p className="text-stone-500 text-sm mt-1">Project Management Software</p>
          </div>

          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto bg-stone-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-7 h-7 text-stone-600" />
                </div>
                <h2 className="text-xl font-medium text-stone-900 mb-2">Resend Verification Email</h2>
                <p className="text-stone-500 text-sm">
                  Enter your email address and we'll send you a new verification link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="resend-verification-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white"
                  disabled={loading}
                  data-testid="resend-verification-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification Link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-medium text-stone-900 mb-2">Check Your Email</h2>
              <p className="text-stone-500 mb-2">
                If an unverified account exists with <strong>{email}</strong>, you'll receive a verification link shortly.
              </p>
              <p className="text-sm text-stone-400 mb-6">
                The link will expire in 24 hours.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setSubmitted(false)}
                className="w-full"
              >
                Try another email
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-stone-500 hover:text-stone-700 inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
